import { maunaMissionAPI } from '@/api';
import MaunaHeader from './MaunaHeader';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { palette } from '@/modules/defines/styles';
import { LoadingMessage } from '@/components/CommonStyles';

interface Reward {
  id: number;
  title: string;
  category: number;
  description: string;
  adminCode: string;
}

interface Mission {
  id: number;
  title: string;
  publish: boolean;
  count: number;
  category: number;
  type: number;
  auto: boolean;
  autoComplete: boolean;
  needReset: boolean;
  description: string;
  howto: string;
  adminCode: string;
  createdAt: string;
  ruleType: string;
  ruleValue: string;
  value?: string;
  envVariableTitle: string | null;
  reward?: Reward[];
  rewards?: Reward[];
}

type RewardCodeUpdate = Pick<Reward, 'id' | 'adminCode'>;

type MissionUpdatePayload = Partial<Mission> & {
  rewardIds?: number[];
  rewardCodeUpdates?: RewardCodeUpdate[];
};

const categoryMap: Record<number, string> = {
  1: '서비스 내 행동유도',
  2: '장소 방문형',
  3: '보물찾기',
  4: '걸음 인식',
};

const typeMap: Record<number, string> = {
  1: '일반미션',
  2: '관리자승인미션',
};

const rewardCategoryMap: Record<number, string> = {
  1: '커피',
  2: '시설이용 할인권',
  3: '굿즈',
};

const decodeAdminCode = (value?: string | null) => {
  if (!value) return '';

  try {
    const binary = window.atob(value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const decoded = new TextDecoder().decode(bytes);

    return decoded.includes('\uFFFD') ? value : decoded;
  } catch {
    return value;
  }
};

const MaunaMissionDetailContainer = () => {
  const { id } = useParams();
  const missionId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<Mission>>({});
  const [selectedRewardIds, setSelectedRewardIds] = useState<number[]>([]);
  const [rewardAdminCodes, setRewardAdminCodes] = useState<
    Record<number, string>
  >({});

  const { data: missionsData, isLoading: isLoadingMissions } = useQuery({
    queryKey: ['getMissionsMauna'],
    queryFn: () =>
      maunaMissionAPI.getMissions({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  const { data: rewardsData, isLoading: isLoadingRewards } = useQuery({
    queryKey: ['getRewardsMauna'],
    queryFn: async () => {
      const response = await maunaMissionAPI.getRewards({
        code: import.meta.env.VITE_SECRET_API_KEY,
      });
      // console.log('=== getRewards 응답 구조 ===');
      // console.log('전체 응답:', response);
      // console.log('response.data:', response?.data);
      // console.log('response.data?.result:', response?.data?.result);
      // console.log('response.data?.data:', response?.data?.data);
      return response;
    },
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  const missions = useMemo(() => {
    const payload = missionsData?.data;
    const list =
      payload?.data?.data ?? payload?.data ?? payload?.result ?? payload ?? [];
    return Array.isArray(list) ? list : [];
  }, [missionsData?.data]);

  const rewards = useMemo(() => {
    const payload = rewardsData?.data;
    const list =
      payload?.data?.data ?? payload?.data ?? payload?.result ?? payload ?? [];
    return Array.isArray(list) ? list : [];
  }, [rewardsData?.data]);

  const mission = useMemo<Mission | undefined>(() => {
    const found = missions.find((item: Mission) => item.id === missionId);
    return found;
  }, [missions, missionId]);

  useEffect(() => {
    if (mission) {
      setFormData({
        ...mission,
        adminCode: decodeAdminCode(mission.adminCode),
      });
      // reward 또는 rewards 필드에서 리워드 배열 가져오기
      const rewardArray = mission.reward || mission.rewards || [];
      const rewardIds = rewardArray.map((r) => r.id);
      setSelectedRewardIds(rewardIds);
    }
  }, [mission]);

  useEffect(() => {
    const nextCodes: Record<number, string> = {};
    rewards.forEach((reward: Reward) => {
      nextCodes[reward.id] = decodeAdminCode(reward.adminCode);
    });
    setRewardAdminCodes(nextCodes);
  }, [rewards]);

  const updateMutation = useMutation({
    mutationFn: (data: MissionUpdatePayload) =>
      maunaMissionAPI.updateMission(
        {
          code: import.meta.env.VITE_SECRET_API_KEY,
          id: missionId,
        },
        data
      ),
    onSuccess: () => {
      alert('수정되었습니다');
      queryClient.invalidateQueries({ queryKey: ['getMissionsMauna'] });
      queryClient.invalidateQueries({ queryKey: ['getRewardsMauna'] });
    },
  });

  const handleChange = <K extends keyof Mission>(
    name: K,
    value: Mission[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggle = (name: keyof Mission) => {
    setFormData((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleRewardToggle = (rewardId: number) => {
    setSelectedRewardIds((prev) => {
      if (prev.includes(rewardId)) {
        return prev.filter((id) => id !== rewardId);
      }
      return [...prev, rewardId];
    });
  };

  const handleRewardAdminCodeChange = (rewardId: number, value: string) => {
    setRewardAdminCodes((prev) => ({
      ...prev,
      [rewardId]: value,
    }));
  };

  const handleSave = () => {
    const selectedRewards = rewards
      .filter((reward: Reward) => selectedRewardIds.includes(reward.id))
      .map((reward: Reward) => ({
        ...reward,
        adminCode:
          rewardAdminCodes[reward.id] ?? decodeAdminCode(reward.adminCode),
      }));

    const payload: MissionUpdatePayload = {
      ...formData,
      id: missionId,
      rewards: selectedRewards,
      rewardIds: selectedRewardIds,
      rewardCodeUpdates: rewards.map((reward: Reward) => ({
        id: reward.id,
        adminCode:
          rewardAdminCodes[reward.id] ?? decodeAdminCode(reward.adminCode),
      })),
      // ruleValue를 저장하고 value는 제거 (또는 ruleValue로 통일)
      ruleValue: formData.ruleValue || formData.value || '',
    };
    // value 필드는 제거 (API가 ruleValue를 사용한다면)
    delete payload.value;

    updateMutation.mutate(payload);
  };

  if (isLoadingMissions || isLoadingRewards) {
    return (
      <Container>
        <MaunaHeader />
        <Content>
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        </Content>
      </Container>
    );
  }

  if (!mission) {
    return (
      <Container>
        <MaunaHeader />
        <Content>
          <LoadingMessage>미션 데이터가 없습니다.</LoadingMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <MaunaHeader />
      <Content>
        <HeaderRow>
          <HeaderTitle>미션 상세</HeaderTitle>
          <HeaderActions>
            <SecondaryButton type="button" onClick={() => navigate(-1)}>
              목록
            </SecondaryButton>
            <PrimaryButton type="button" onClick={handleSave}>
              저장
            </PrimaryButton>
          </HeaderActions>
        </HeaderRow>
        <Layout>
          <FormColumn>
            <FormSection>
              <FormGroup>
                <Label>미션명</Label>
                <Input
                  value={formData.title || ''}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </FormGroup>
              <TwoColumn>
                <FormGroup>
                  <Label>횟수</Label>
                  <Input
                    type="number"
                    value={formData.count ?? 0}
                    onChange={(e) =>
                      handleChange('count', Number(e.target.value))
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <Label>카테고리</Label>
                  <Select
                    value={String(formData.category ?? 1)}
                    onChange={(e) =>
                      handleChange('category', Number(e.target.value))
                    }
                  >
                    {Object.entries(categoryMap).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              </TwoColumn>
              <TwoColumn>
                <FormGroup>
                  <Label>타입</Label>
                  <Select
                    value={String(formData.type ?? 1)}
                    onChange={(e) =>
                      handleChange('type', Number(e.target.value))
                    }
                  >
                    {Object.entries(typeMap).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>생성 날짜</Label>
                  <Input
                    value={
                      formData.createdAt
                        ? new Date(formData.createdAt).toLocaleDateString()
                        : ''
                    }
                    readOnly
                  />
                </FormGroup>
              </TwoColumn>
              <SwitchGroup>
                <SwitchItem>
                  <SwitchLabel>앱 노출</SwitchLabel>
                  <SwitchControl>
                    <SwitchInput
                      type="checkbox"
                      checked={!!formData.publish}
                      onChange={() => handleToggle('publish')}
                    />
                    <SwitchSlider $active={!!formData.publish} />
                    <SwitchStatus>
                      {formData.publish ? 'ON' : 'OFF'}
                    </SwitchStatus>
                  </SwitchControl>
                </SwitchItem>
                <SwitchItem>
                  <SwitchLabel>자동 시작</SwitchLabel>
                  <SwitchControl>
                    <SwitchInput
                      type="checkbox"
                      checked={!!formData.auto}
                      onChange={() => handleToggle('auto')}
                    />
                    <SwitchSlider $active={!!formData.auto} />
                    <SwitchStatus>{formData.auto ? 'ON' : 'OFF'}</SwitchStatus>
                  </SwitchControl>
                </SwitchItem>
                <SwitchItem>
                  <SwitchLabel>자동 완료</SwitchLabel>
                  <SwitchControl>
                    <SwitchInput
                      type="checkbox"
                      checked={!!formData.autoComplete}
                      onChange={() => handleToggle('autoComplete')}
                    />
                    <SwitchSlider $active={!!formData.autoComplete} />
                    <SwitchStatus>
                      {formData.autoComplete ? 'ON' : 'OFF'}
                    </SwitchStatus>
                  </SwitchControl>
                </SwitchItem>
                <SwitchItem>
                  <SwitchLabel>월 리셋</SwitchLabel>
                  <SwitchControl>
                    <SwitchInput
                      type="checkbox"
                      checked={!!formData.needReset}
                      onChange={() => handleToggle('needReset')}
                    />
                    <SwitchSlider $active={!!formData.needReset} />
                    <SwitchStatus>
                      {formData.needReset ? 'ON' : 'OFF'}
                    </SwitchStatus>
                  </SwitchControl>
                </SwitchItem>
              </SwitchGroup>
              <FormGroup>
                <Label>설명</Label>
                <TextArea
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>How To (이미지 링크)</Label>
                <Input
                  value={formData.howto || ''}
                  onChange={(e) => handleChange('howto', e.target.value)}
                />
                {formData.howto && (
                  <LinkPreview
                    href={formData.howto}
                    target="_blank"
                    rel="noreferrer"
                  >
                    링크 열기
                  </LinkPreview>
                )}
              </FormGroup>
              {Number(formData.type) === 2 && (
                <FormGroup>
                  <Label>관리자 승인 코드</Label>
                  <Input
                    value={formData.adminCode || ''}
                    onChange={(e) =>
                      handleChange('adminCode', e.target.value)
                    }
                  />
                </FormGroup>
              )}
              {formData.envVariableTitle !== null && (
                <TwoColumn>
                  <FormGroup>
                    <Label>룰 타입</Label>
                    <Select
                      value={formData.ruleType || 'number_range'}
                      onChange={(e) => handleChange('ruleType', e.target.value)}
                    >
                      <option value="number_range">범위</option>
                      <option value="number">지정</option>
                    </Select>
                  </FormGroup>
                  {formData.ruleType === 'number' ? (
                    <FormGroup>
                      <Label>값</Label>
                      <NumberInputWrapper>
                        <NumberInput
                          type="number"
                          value={formData.ruleValue || formData.value || ''}
                          onChange={(e) =>
                            handleChange('ruleValue', e.target.value)
                          }
                        />
                        <NumberButtons>
                          <NumberButton
                            type="button"
                            onClick={() => {
                              const currentValue =
                                formData.ruleValue || formData.value || '';
                              const current = Number(currentValue) || 0;
                              handleChange('ruleValue', String(current + 1));
                            }}
                          >
                            ▲
                          </NumberButton>
                          <NumberButton
                            type="button"
                            onClick={() => {
                              const currentValue =
                                formData.ruleValue || formData.value || '';
                              const current = Number(currentValue) || 0;
                              handleChange(
                                'ruleValue',
                                String(Math.max(0, current - 1))
                              );
                            }}
                          >
                            ▼
                          </NumberButton>
                        </NumberButtons>
                      </NumberInputWrapper>
                    </FormGroup>
                  ) : (
                    <TwoColumn>
                      <FormGroup>
                        <Label>최소값</Label>
                        <Input
                          type="number"
                          value={
                            (
                              formData.ruleValue ||
                              formData.value ||
                              ''
                            )?.includes(',')
                              ? (
                                  formData.ruleValue ||
                                  formData.value ||
                                  ''
                                ).split(',')[0]
                              : formData.ruleValue || formData.value || ''
                          }
                          onChange={(e) => {
                            const currentValue =
                              formData.ruleValue || formData.value || '';
                            const max = currentValue?.includes(',')
                              ? currentValue.split(',')[1]
                              : '';
                            handleChange(
                              'ruleValue',
                              max ? `${e.target.value},${max}` : e.target.value
                            );
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>최대값</Label>
                        <Input
                          type="number"
                          value={
                            (
                              formData.ruleValue ||
                              formData.value ||
                              ''
                            )?.includes(',')
                              ? (
                                  formData.ruleValue ||
                                  formData.value ||
                                  ''
                                ).split(',')[1]
                              : ''
                          }
                          onChange={(e) => {
                            const currentValue =
                              formData.ruleValue || formData.value || '';
                            const min = currentValue?.includes(',')
                              ? currentValue.split(',')[0]
                              : currentValue || '';
                            handleChange(
                              'ruleValue',
                              min
                                ? `${min},${e.target.value}`
                                : `,${e.target.value}`
                            );
                          }}
                        />
                      </FormGroup>
                    </TwoColumn>
                  )}
                </TwoColumn>
              )}
            </FormSection>
          </FormColumn>
          <RewardColumn>
            <RewardHeader>Reward</RewardHeader>
            <RewardList>
              {rewards.map((reward: Reward) => {
                const isChecked = selectedRewardIds.includes(reward.id);

                return (
                  <RewardCard key={reward.id}>
                    <RewardTitle>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleRewardToggle(reward.id)}
                      />
                      <span>{reward.title}</span>
                    </RewardTitle>
                    <RewardMeta>
                      카테고리:{' '}
                      {rewardCategoryMap[reward.category] || reward.category}
                    </RewardMeta>
                    <RewardMeta>{reward.description}</RewardMeta>
                    <RewardCodeGroup>
                      <RewardCodeLabel>승인코드</RewardCodeLabel>
                      <RewardCodeInput
                        value={
                          rewardAdminCodes[reward.id] ??
                          decodeAdminCode(reward.adminCode)
                        }
                        onChange={(e) =>
                          handleRewardAdminCodeChange(
                            reward.id,
                            e.target.value
                          )
                        }
                      />
                    </RewardCodeGroup>
                  </RewardCard>
                );
              })}
            </RewardList>
          </RewardColumn>
        </Layout>
      </Content>
    </Container>
  );
};

export default MaunaMissionDetailContainer;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
`;

const Content = styled.main`
  flex: 1;
  padding: 2rem;
  background-color: #f5f5f5;
  overflow-y: auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 6px;
  padding: 0.6rem 1.2rem;
  background-color: ${palette.systemGreen};
  color: white;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #3e8e41;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.6rem 1.2rem;
  background-color: white;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 1.5rem;
`;

const FormColumn = styled.div`
  min-width: 0;
`;

const RewardColumn = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  height: fit-content;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
`;

const RewardHeader = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 700;
`;

const RewardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RewardCard = styled.div`
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: #fafafa;
`;

const RewardTitle = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-weight: 600;
  margin-bottom: 0.4rem;
`;

const RewardMeta = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.4;
`;

const RewardCodeGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const RewardCodeLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #4b5563;
`;

const RewardCodeInput = styled.input`
  width: 100%;
  padding: 0.45rem 0.55rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.85rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${palette.systemGreen};
  }
`;

const FormSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: ${palette.systemGreen};
  }
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  min-height: 120px;
  resize: vertical;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: ${palette.systemGreen};
  }
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${palette.systemGreen};
  }
`;

const SwitchGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SwitchItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SwitchLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
`;

const SwitchControl = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const SwitchInput = styled.input`
  display: none;
`;

const SwitchSlider = styled.span<{ $active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background-color: ${({ $active }) =>
    $active ? palette.systemGreen : '#d1d5db'};
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $active }) => ($active ? '22px' : '3px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const SwitchStatus = styled.span`
  font-size: 0.85rem;
  color: #6b7280;
`;

const LinkPreview = styled.a`
  font-size: 0.85rem;
  color: ${palette.systemBlue};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const NumberInputWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const NumberInput = styled.input`
  flex: 1;
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: ${palette.systemGreen};
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
`;

const NumberButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NumberButton = styled.button`
  width: 32px;
  height: 20px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #374151;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: ${palette.systemGreen};
  }

  &:active {
    background: #e5e7eb;
  }
`;
